import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { MenuPage } from '../../layouts';
import { routes } from '../../routes/Routes';
import { makeGet, makeFileUpload } from '../../api/ajax';
import { useSubscriptionLimit, FEATURES } from '../../hooks';
import { constants as subscriptionConstants } from '../../redux/subscriptions';
import { PageTitle, InfoModal, Accordion, Toggle } from '../../components';
import {
  actions as customerConfigurationsActions,
  selectors as customerConfigurationsSelectors,
  constants as customerConfigurationsConstants
} from '../../redux/customerConfigurations';

import ThemeSettings from './containers/ThemeSettings/ThemeSettings';

const BrandingPageTemplate = ({ children, isLoading }) => (
  <MenuPage currentRoute={routes.branding}>
    <PageTitle>
      <PageTitle.Left headingText="Theme tool - your branding" isLoading={isLoading} />
    </PageTitle>
    {children}
  </MenuPage>
);

BrandingPageTemplate.propTypes = {
  children: PropTypes.node.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

const ACCORDION_KEYS = {
  DEFAULT: 'default',
  DARK: 'dark',
};

const Customization = () => {
  const dispatch = useDispatch();
  const customerConfiguration = useSelector(customerConfigurationsSelectors.selectCustomerConfiguration);
  const isLoading = !customerConfiguration;

  const [activeKey, setActiveKey] = useState(ACCORDION_KEYS.DEFAULT);
  const [darkThemeEnabled, setDarkThemeEnabled] = useState(false);

  const { isFeatureDisabled: isThemingSubscriptionLimited } = useSubscriptionLimit(FEATURES.customTheme);

  useEffect(() => {
    if (!customerConfiguration) {
      makeGet('customerConfigurations/current').subscribe(({ response }) => {
        dispatch(customerConfigurationsActions.loadCurrentCustomerConfigurationSuccess(response));
      });
    } else {
      const darkThemeEnabled = customerConfiguration.themes && customerConfiguration.themes.dark !== null;
      setDarkThemeEnabled(darkThemeEnabled);
      setActiveKey(
        darkThemeEnabled
          ? ACCORDION_KEYS.DARK
          : ACCORDION_KEYS.DEFAULT
      );
    }
  }, [customerConfiguration, dispatch]);

  return (
    <BrandingPageTemplate isLoading={isLoading}>
      {
        isThemingSubscriptionLimited && (
          <InfoModal
            type="warning"
            message={subscriptionConstants.SUBSCRIPTION_MESSAGES.cannotConfigureCustomTheme}
            shownModal={isThemingSubscriptionLimited}
          />
        )
      }
      {
        !isThemingSubscriptionLimited && (
          <>
            <div className="d-flex align-items-center mb-4 justify-content-end">
              <Toggle
                on={darkThemeEnabled}
                toggleClick={(enabled) => {
                  setActiveKey(enabled ? ACCORDION_KEYS.DARK : ACCORDION_KEYS.DEFAULT);
                  setDarkThemeEnabled(enabled);

                  if (!enabled) {
                    makeFileUpload('customerConfigurations/current/themingSettings', null, { disableDarkTheme: true }, 'logoFile').subscribe(
                      ({ response }) => {
                        dispatch(customerConfigurationsActions.loadCurrentCustomerConfigurationSuccess(response));
                      }
                    );
                  }
                }}
              />
              <span className="ml-1">Enable dark theme</span>
            </div>
            <Accordion
              defaultActiveKey={ACCORDION_KEYS.DEFAULT}
              activeKey={activeKey}
              onSelect={(key) => setActiveKey(key)}
            >
              <span key={ACCORDION_KEYS.DEFAULT}>
                <Accordion.Header
                  eventKey={ACCORDION_KEYS.DEFAULT}
                  childrenSize={6}
                  caretSize={6}
                  onClick={() => setActiveKey(ACCORDION_KEYS.DEFAULT)}
                  noFocus
                >
                  <span>
                    <h5>{customerConfigurationsConstants.THEME_NAME[customerConfigurationsConstants.THEME.DEFAULT]}</h5>
                  </span>
                </Accordion.Header>
                <Accordion.Body
                  eventKey={ACCORDION_KEYS.DEFAULT}
                  borderType="none"
                >
                  <div>
                    <ThemeSettings themeType={customerConfigurationsConstants.THEME.DEFAULT} />
                  </div>
                </Accordion.Body>
              </span>
              {
                darkThemeEnabled && (
                  <span key={ACCORDION_KEYS.DARK}>
                    <Accordion.Header
                      eventKey={ACCORDION_KEYS.DARK}
                      childrenSize={6}
                      caretSize={6}
                      onClick={() => setActiveKey(ACCORDION_KEYS.DARK)}
                      noFocus
                    >
                      <span>
                        <h5>{customerConfigurationsConstants.THEME_NAME[customerConfigurationsConstants.THEME.DARK]}</h5>
                      </span>
                    </Accordion.Header>
                    <Accordion.Body
                      eventKey={ACCORDION_KEYS.DARK}
                      borderType="none"
                    >
                      <div>
                        <ThemeSettings themeType={customerConfigurationsConstants.THEME.DARK} />
                      </div>
                    </Accordion.Body>
                  </span>
                )
              }
            </Accordion>
          </>
        )
      }
    </BrandingPageTemplate>
  );
};

export default Customization;
